export interface CommandToken {
  value: string;
  raw: string;
  start: number;
  end: number;
  quoteChar?: "\"" | "'";
  isClosedQuote: boolean;
}

export interface TokenizedCommandInput {
  tokens: CommandToken[];
  hasTrailingWhitespace: boolean;
  hasUnterminatedQuote: boolean;
}

export interface CommandSegment {
  value: string;
  start: number;
  end: number;
}

export function getCommandSegmentAtCursor(input: string, cursorPosition: number): CommandSegment {
  const text = String(input ?? "");
  const cursor = Math.max(0, Math.min(cursorPosition, text.length));
  let segmentStart = 0;
  let quoteChar: "\"" | "'" | undefined;

  for (let index = 0; index < text.length; index += 1) {
    const currentChar = text[index];
    if (quoteChar) {
      if (currentChar === "\\") {
        index += 1;
      } else if (currentChar === quoteChar) {
        quoteChar = undefined;
      }
      continue;
    }

    if (currentChar === "\"" || currentChar === "'") {
      quoteChar = currentChar;
      continue;
    }

    if (currentChar === ";") {
      if (cursor <= index) {
        return { value: text.slice(segmentStart, index), start: segmentStart, end: index };
      }
      segmentStart = index + 1;
    }
  }

  return { value: text.slice(segmentStart), start: segmentStart, end: text.length };
}

export function splitCommandBatch(input: string): string[] {
  const text = String(input ?? "");
  const commands: string[] = [];
  let commandStart = 0;
  let quoteChar: "\"" | "'" | undefined;

  for (let index = 0; index < text.length; index += 1) {
    const currentChar = text[index];

    if (quoteChar) {
      if (currentChar === "\\") {
        index += 1;
      } else if (currentChar === quoteChar) {
        quoteChar = undefined;
      }
      continue;
    }

    if (currentChar === "\"" || currentChar === "'") {
      quoteChar = currentChar;
    } else if (currentChar === ";") {
      commands.push(text.slice(commandStart, index).trim());
      commandStart = index + 1;
    }
  }

  commands.push(text.slice(commandStart).trim());
  return commands;
}

const unescapeQuotedValue = (value: string, quoteChar: "\"" | "'"): string =>
  value.replace(/\\(.)/g, (_match, capturedChar: string) => {
    if (capturedChar === quoteChar || capturedChar === "\\") {
      return capturedChar;
    }

    return capturedChar;
  });

export function tokenizeCommandInput(input: string): TokenizedCommandInput {
  const text = String(input ?? "");
  const tokens: CommandToken[] = [];
  let index = 0;
  let hasUnterminatedQuote = false;

  while (index < text.length) {
    while (index < text.length && /\s/u.test(text[index])) {
      index += 1;
    }

    if (index >= text.length) {
      break;
    }

    const tokenStart = index;
    const firstChar = text[index];

    if (firstChar === "\"" || firstChar === "'") {
      const quoteChar = firstChar as "\"" | "'";
      index += 1;
      let tokenValue = "";
      let isClosedQuote = false;

      while (index < text.length) {
        const currentChar = text[index];

        if (currentChar === "\\" && index + 1 < text.length) {
          tokenValue += text[index + 1];
          index += 2;
          continue;
        }

        if (currentChar === quoteChar) {
          isClosedQuote = true;
          index += 1;
          break;
        }

        tokenValue += currentChar;
        index += 1;
      }

      if (!isClosedQuote) {
        hasUnterminatedQuote = true;
      }

      const tokenEnd = index;
      tokens.push({
        value: isClosedQuote ? unescapeQuotedValue(tokenValue, quoteChar) : tokenValue,
        raw: text.slice(tokenStart, tokenEnd),
        start: tokenStart,
        end: tokenEnd,
        quoteChar,
        isClosedQuote,
      });
      continue;
    }

    while (index < text.length && !/\s/u.test(text[index])) {
      index += 1;
    }

    const tokenEnd = index;
    tokens.push({
      value: text.slice(tokenStart, tokenEnd),
      raw: text.slice(tokenStart, tokenEnd),
      start: tokenStart,
      end: tokenEnd,
      isClosedQuote: false,
    });
  }

  return {
    tokens,
    hasTrailingWhitespace: /\s$/u.test(text),
    hasUnterminatedQuote,
  };
}
